/**
 * Capacidade da Fábrica (Legado): quanto da capacidade de cada mês,
 * quinzena e semana a fila de hoje já toma, onde está a próxima vaga, quanto
 * a fábrica de fato produziu e o quanto os números erram.
 *
 * Plano: docs/plano-capacidade-produtiva-fabrica-2026-10-07.md (decisões do
 * dono na seção 3). Regras em `utils/capacidade` (puras e testadas); aqui só
 * a apresentação, no desenho dos Relatórios: lista limpa, um sinal de cor
 * por linha, todo número com a régua do que mede e de quanto erra.
 *
 * O cálculo roda no navegador sobre a planilha inteira (decisão 4); o
 * histórico (términos de anos anteriores e fotos para a precisão) e o
 * calendário da unidade vêm do backend.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FiAlertTriangle, FiInfo, FiRepeat, FiTrendingUp } from "react-icons/fi";
import { Link, useNavigate, useSearchParams } from "react-router-dom";

import AmbienteDetalhesDrawer from "../../components/AmbienteDetalhesDrawer";
import { EmptyState, EmptyStateErro } from "../../components/EmptyState";
import BotaoExportacao from "../../components/Exportacao";
import { SoIcone } from "../../components/ListaCompacta/styles";
import { PageLoader } from "../../components/LoadingMarcenaria";
import { useAuth } from "../../context/AuthContext";
import { FONTE_SERVIDOR } from "../../context/DadosContext";
import { isExportModeFromQuery, useExportRender } from "../../context/ExportRenderContext";
import { PERM_MARCAR_EXCECAO } from "../../hooks/useExcecoesCapacidade";
import { useExportSnapshot } from "../../hooks/useExportSnapshot";
import useMediaQuery from "../../hooks/useMediaQuery";
import { mq } from "../../styles/canonical/breakpoints";
import { ESCALA, MOTIVO_RITMO, atrasados } from "../../utils/capacidade";
import { F } from "../../utils/gerencialFields";
import AbaExcecoes from "./AbaExcecoes";
import AbaPrecisao from "./AbaPrecisao";
import AbaQualidade from "./AbaQualidade";
import AbaVendasAssinadas from "./AbaVendasAssinadas";
import {
  ABA_EXCECOES,
  ESCALAS,
  abasDaPagina,
  ddmm,
  formatarVersaoPlanilha,
  montarExportacao,
  montarIndicadores,
  montarPeriodos,
  textoFaixaDePrecisao,
  textoFonteDaPrecisao,
  textoMotivoSemRitmo,
  textoSegundaLeitura,
} from "./apresentacao";
import { VEREDITO_NA_TELA, secaoDaSimulacao, textoDaEntrada } from "./apresentacaoSimulacao";
import { ABA_VENDAS, secaoDasVendas, textoDoAvisoDeSugestoes } from "./apresentacaoVendas";
import BlocoAtrasados from "./BlocoAtrasados";
import BlocoConferencia from "./BlocoConferencia";
import CenarioPcp from "./CenarioPcp";
import { CancelarVendaAssinada, LigarVendaAssinada } from "./LigarVendaAssinada";
import { ReprogramarContext } from "./contextoReprogramar";
import Periodos from "./Periodos";
import RegistrarVendaAssinada from "./RegistrarVendaAssinada";
import SimularVenda from "./SimularVenda";
import {
  Aviso,
  BlocoTitulo,
  BotaoTexto,
  Controles,
  Indicador,
  Indicadores,
  LinhaControles,
  LinhaTitulo,
  Pagina,
  Ponto,
  Regua,
  Segmentado,
  Topo,
} from "./styles";
import { BotaoAcao } from "./stylesSimulacao";
import useCapacidadeFabrica from "./useCapacidadeFabrica";
import useMarcasReprogramacao from "./useMarcasReprogramacao";
import usePreferencias from "./usePreferencias";
import { useFeriadosDaPlanilha, useReguasDoFluxo, useSimulacao } from "./useSimulacao";
import useVendasNaPagina from "./useVendasNaPagina";
import VisaoDia from "./VisaoDia";

export const PAINEL_EXPORTACAO = "capacidade-fabrica";
export const PERM_LER_PLANILHA = "planilha_legado:view";
export const PERM_CONFIGURAR_PLANILHA = "planilha_legado:configurar";
export const PERM_MAPA = "mapa_producao:view";
export const PERM_REPROGRAMAR = "capacidade_fabrica:reprogramar";
export const ROTA_CALENDARIO = "/configuracoes/calendario";
const ROTA_PLANILHAS = "/configuracoes/planilhas";

const SEM_PERIODOS = Object.freeze({ proximos: [], produzidos: [] });
const ABA_OCUPACAO = "ocupacao";

/**
 * Seções visuais da exportação: SÓ as que estão montadas agora. O agendador
 * (iframe) espera todas as declaradas aparecerem e estoura o tempo se faltar
 * uma; a exportação manual descartaria a que falta sem aviso.
 */
function secoesVisuais({ mostraPeriodos, temProduzido, temAtrasados }) {
  return [
    { id: "indicadores", label: "Resumo", selector: "[data-pdf='capacidade-indicadores']" },
    mostraPeriodos && { id: "periodos", label: "Agora e próximos", selector: "[data-pdf='capacidade-periodos']" },
    mostraPeriodos &&
      temProduzido && { id: "produzido", label: "Produzido", selector: "[data-pdf='capacidade-produzido']" },
    temAtrasados && { id: "atrasados", label: "Atrasados", selector: "[data-pdf='capacidade-atrasados']" },
  ].filter(Boolean);
}

function subtituloDaPlanilha(planilha) {
  const versao = formatarVersaoPlanilha(planilha.atualizadoEm);
  const origem = planilha.fonte === FONTE_SERVIDOR ? "Drive" : "arquivo";
  return versao ? `Planilha de ${versao} · ${origem}` : `Planilha · ${origem}`;
}

function SemPlanilha({ podeLerPlanilha, temLinhas, onAbrirPlanilhas }) {
  let descricao =
    "A capacidade é calculada a partir da planilha Gerenciador de Produção. Carregue a planilha em " +
    "Configurações > Planilhas (com o Google Drive ligado, ela abre sozinha em qualquer aparelho).";
  if (temLinhas) {
    descricao =
      "A planilha foi carregada por um caminho que ainda não traz a leitura da capacidade. Use " +
      "\"Atualizar dados\" em Configurações > Planilhas.";
  } else if (!podeLerPlanilha) {
    descricao =
      "A capacidade é calculada a partir da planilha Gerenciador de Produção, e sua conta ainda não pode " +
      "lê-la do Google Drive. Peça ao administrador a permissão de ver a planilha do Legado, ou carregue o " +
      "arquivo em Configurações > Planilhas.";
  }
  return (
    <EmptyState
      tone="info"
      titulo="Sem planilha para calcular"
      descricao={descricao}
      acoes={[{ label: "Abrir Configurações > Planilhas", onClick: onAbrirPlanilhas }]}
    />
  );
}

function LayoutNaoReconhecido({ painel }) {
  const faltando = painel?.qualidade?.itens?.[0]?.faltando || [];
  const descricao = faltando.length
    ? `Não encontramos estas colunas na aba GERENCIAL: ${faltando.join(", ")}. `
    : "A aba GERENCIAL não está no formato esperado. ";
  return (
    <EmptyState
      tone="warn"
      titulo="A planilha mudou de formato"
      descricao={
        `${descricao}A página fica bloqueada até a planilha voltar ao formato esperado, ` +
        "para não mostrar números errados."
      }
    />
  );
}

function Avisos({ painel }) {
  const motivo = textoMotivoSemRitmo(painel.ritmo);
  const { planilhaParada, incompleto, percentualCritico } = painel.qualidade || {};
  return (
    <>
      {motivo ? (
        <Aviso $tom="warning" role="note">
          <FiAlertTriangle size={14} aria-hidden="true" />
          <div>
            <strong>Sem ritmo medido, sem ocupação.</strong> {motivo}
            {painel.ritmo.motivo === MOTIVO_RITMO.CALENDARIO_AUSENTE ? (
              <>
                {" "}
                <Link to={ROTA_CALENDARIO}>Abrir Configurações &gt; Calendário</Link>
              </>
            ) : null}
          </div>
        </Aviso>
      ) : null}
      {planilhaParada ? (
        <Aviso $tom="neutral" role="note">
          <FiInfo size={14} aria-hidden="true" />
          <div>
            <strong>Planilha parada.</strong> O último lançamento (corte, término ou carregamento) é de{" "}
            {ddmm(planilhaParada.desde)}, há {planilhaParada.du} dias úteis. A ocupação fica em cinza até a planilha
            voltar a ser atualizada.
          </div>
        </Aviso>
      ) : null}
      {incompleto ? (
        <Aviso $tom="neutral" role="note">
          <FiInfo size={14} aria-hidden="true" />
          <div>
            <strong>Fila incompleta.</strong> {percentualCritico}% da fila tem problema crítico na planilha (aba
            Qualidade). A ocupação fica em cinza: o número existe, mas não dá para confiar nele.
          </div>
        </Aviso>
      ) : null}
    </>
  );
}

function FaixaIndicadores({ indicadores }) {
  return (
    <Indicadores data-pdf="capacidade-indicadores" aria-label="Resumo">
      {indicadores.map((indicador) => (
        <Indicador key={indicador.id} data-indicador={indicador.id} role="group" aria-label={indicador.rotulo}>
          <span className="rot">
            {indicador.tom ? <Ponto $tom={indicador.tom} aria-hidden="true" /> : null}
            {indicador.rotulo}
          </span>
          <span className="num">{indicador.valor}</span>
          {indicador.complemento ? <span className="comp">{indicador.complemento}</span> : null}
          {indicador.regua ? <span className="regua">{indicador.regua}</span> : null}
        </Indicador>
      ))}
    </Indicadores>
  );
}

function SeletorOpcoes({ opcoes, valor, onChange, rotulo, papel = "group" }) {
  const ehAbas = papel === "tablist";
  return (
    <Segmentado role={papel} aria-label={rotulo}>
      {opcoes.map((opcao) => (
        <button
          key={opcao.id}
          type="button"
          role={ehAbas ? "tab" : undefined}
          aria-selected={ehAbas ? valor === opcao.id : undefined}
          aria-pressed={ehAbas ? undefined : valor === opcao.id}
          onClick={() => onChange(opcao.id)}
        >
          {opcao.rotulo}
        </button>
      ))}
    </Segmentado>
  );
}

/** Simulação ativa com o painel fechado: uma linha, para abrir ou limpar. */
function SimulacaoAtiva({ entrada, resultado, onAbrir, onLimpar }) {
  if (!entrada) return null;
  const temVeredito = resultado && !resultado.indisponivel && !resultado.erroEntrada;
  const veredito = temVeredito ? VEREDITO_NA_TELA[resultado.veredito] : null;
  return (
    <Aviso $tom={veredito?.tom || "neutral"} role="note" aria-label="Simulação ativa">
      <FiTrendingUp size={14} aria-hidden="true" />
      <div>
        <strong>Simulação ativa:</strong> {textoDaEntrada(entrada)}
        {veredito ? ` · ${veredito.frase}` : ""}.{" "}
        <BotaoTexto type="button" onClick={onAbrir} style={{ minHeight: 0, padding: "0 4px" }}>
          Abrir
        </BotaoTexto>
        <BotaoTexto type="button" onClick={onLimpar} style={{ minHeight: 0, padding: "0 4px" }}>
          Limpar
        </BotaoTexto>
      </div>
    </Aviso>
  );
}

/** Venda assinada que parece já estar na planilha: a obra pode contar duas vezes. */
function AvisoDeSugestoes({ quantidade, onVer }) {
  const texto = textoDoAvisoDeSugestoes(quantidade);
  if (!texto) return null;
  return (
    <Aviso $tom="warning" role="note" aria-label="Vendas assinadas a ligar">
      <FiInfo size={14} aria-hidden="true" />
      <div>
        {texto}{" "}
        <BotaoTexto type="button" onClick={onVer} style={{ minHeight: 0, padding: "0 4px" }}>
          Ver vendas assinadas
        </BotaoTexto>
      </div>
    </Aviso>
  );
}

/**
 * Link de fora (Minhas Pendências, sino) com `?aba=vendas`: abre a aba
 * pedida uma vez e tira o parâmetro, para a aba lembrada voltar a valer.
 */
function useAbaDaUrl({ abas, setAba }) {
  const [params, setParams] = useSearchParams();
  const pedida = params.get("aba");
  useEffect(() => {
    if (!pedida) return;
    if (abas.some((opcao) => opcao.id === pedida)) setAba(pedida);
    setParams(
      (atuais) => {
        const proximos = new URLSearchParams(atuais);
        proximos.delete("aba");
        return proximos;
      },
      { replace: true },
    );
  }, [pedida, abas, setAba, setParams]);
}

function ReguaDaOcupacao({ precisao }) {
  return (
    <Regua>
      Ocupação = parte dos dias úteis do período que a fila de hoje consome no ritmo medido. Erro de{" "}
      {textoFaixaDePrecisao(precisao)} ({textoFonteDaPrecisao(precisao)}). A cor segue o pior caso: abaixo de 80%
      cabe, de 80% a 94% apertado, 95% ou mais fila cheia.
    </Regua>
  );
}

export default function CapacidadeFabrica() {
  const {
    estado,
    erroPlanilha,
    painel,
    hoje,
    calendario,
    historico,
    excecoes,
    vendas,
    linhaDaPlanilha,
    planilha,
    importarCopia,
    recarregar,
  } = useCapacidadeFabrica();
  const { hasPermission } = useAuth();
  const { renderAll } = useExportRender();
  const navigate = useNavigate();
  const compacto = useMediaQuery(mq.tablet);
  const pageRef = useRef(null);
  const preferencias = usePreferencias();
  const { setAba, setEscala } = preferencias;
  const [ambienteAberto, setAmbienteAberto] = useState(null);
  // No iframe do agendador (?export=1) a página mostra o que o arquivo leva
  // (Ocupação, em períodos), seja qual for a aba lembrada neste aparelho.
  const exportandoAgendado = useMemo(() => isExportModeFromQuery(), []);

  const pronto = estado === "pronto" && Boolean(painel?.ok);
  const { naoUteis, anosComRegistro } = calendario;
  const podeReprogramar = Boolean(hasPermission?.(PERM_REPROGRAMAR));
  const abas = useMemo(
    () => abasDaPagina({ podeMarcarExcecao: Boolean(hasPermission?.(PERM_MARCAR_EXCECAO)) }),
    [hasPermission],
  );
  useAbaDaUrl({ abas, setAba });
  // Na visão Dia não há períodos na tela; o arquivo exportado leva os meses.
  const escalaDosPeriodos = preferencias.escala === ESCALA.DIA ? ESCALA.MES : preferencias.escala;
  // Aba lembrada que este perfil não vê (Exceções sem a permissão) volta para Ocupação.
  const abaLembrada = abas.some((opcao) => opcao.id === preferencias.aba) ? preferencias.aba : ABA_OCUPACAO;
  const aba = exportandoAgendado ? ABA_OCUPACAO : abaLembrada;
  const escala = exportandoAgendado ? escalaDosPeriodos : preferencias.escala;
  const mostraPeriodos = aba === ABA_OCUPACAO && escala !== ESCALA.DIA;
  const periodos = useMemo(
    () =>
      pronto ? montarPeriodos({ painel, escala: escalaDosPeriodos, hoje, naoUteis, anosComRegistro }) : SEM_PERIODOS,
    [pronto, painel, escalaDosPeriodos, hoje, naoUteis, anosComRegistro],
  );
  const indicadores = useMemo(() => (pronto ? montarIndicadores(painel) : []), [pronto, painel]);
  const segundaLeitura = pronto ? textoSegundaLeitura(painel) : null;

  // F2: simular venda (todos com :view) e cenário do PCP (:reprogramar).
  const painelPronto = pronto ? painel : null;
  const { feriadosPlanilha, tabelasAusente } = useFeriadosDaPlanilha();
  const { fluxo, folga } = useReguasDoFluxo({ painel: painelPronto, terminosBackend: historico.terminos, naoUteis, hoje });
  const simulacao = useSimulacao({
    painel: painelPronto,
    fluxo,
    G: folga.G,
    anosComRegistro,
    escala: escalaDosPeriodos,
    hoje,
  });
  // O link ?simular= abre a página já com o painel da simulação aberto.
  const [simulacaoAberta, setSimulacaoAberta] = useState(() => Boolean(simulacao.entrada || simulacao.linkInvalido));
  const [cenario, setCenario] = useState({ aberto: false, pedido: "" });
  const abrirCenario = useCallback(
    (grupo) => setCenario({ aberto: true, pedido: grupo ? `${grupo.pedido}|${grupo.cliente || ""}` : "" }),
    [],
  );
  // F4: vendas assinadas aguardando o PCP (já estão no painel, na fila).
  const paineisDeVenda = useVendasNaPagina({
    vendas,
    linhas: painelPronto ? painelPronto.classificacao?.linhas : null,
    hoje,
    aoRegistrarDaSimulacao: simulacao.limpar,
  });
  const { registrarDaSimulacao: abrirRegistroDaSimulacao } = paineisDeVenda;
  const registrarDaSimulacao = useCallback(
    (entrada) => {
      setSimulacaoAberta(false);
      abrirRegistroDaSimulacao(entrada);
    },
    [abrirRegistroDaSimulacao],
  );
  const marcas = useMarcasReprogramacao({
    linhas: painelPronto ? painelPronto.classificacao?.linhas : null,
    hoje,
    feriadosPlanilha,
  });
  const listaAtrasados = useMemo(() => (pronto ? atrasados(painel.abertos, hoje) : []), [pronto, painel, hoje]);

  const podeAbrir = useCallback((item) => Boolean(item?.chave && linhaDaPlanilha.has(item.chave)), [linhaDaPlanilha]);
  const abrirAmbiente = useCallback(
    (item) => {
      const row = item?.chave ? linhaDaPlanilha.get(item.chave) : null;
      if (!row) return;
      setAmbienteAberto({ ambiente: row[F.AMBIENTE], lote: row[F.NUMERO_DO_LOTE] ?? "—", row });
    },
    [linhaDaPlanilha],
  );

  const exportacao = useMemo(() => {
    const conteudo = pronto ? montarExportacao({ painel, periodos, hoje }) : { resumo: [], secoes: [] };
    // Simulação ativa vai junto no arquivo, com a mesma régua da tela; as
    // vendas assinadas também, porque já pesam na fila exportada.
    const secaoVendas = pronto ? secaoDasVendas(painel.vendasAssinadas) : null;
    const secoes = [
      ...conteudo.secoes,
      ...(secaoVendas ? [secaoVendas] : []),
      ...(pronto && simulacao.entrada && simulacao.resultado
        ? [secaoDaSimulacao({ entrada: simulacao.entrada, resultado: simulacao.resultado })]
        : []),
    ];
    return {
      titulo: "Capacidade da Fábrica",
      // Gate do agendador: sem planilha e histórico o snapshot sairia vazio.
      pronto,
      resumo: conteudo.resumo,
      secoes,
      visual: {
        ref: pageRef,
        sections: secoesVisuais({
          mostraPeriodos,
          temProduzido: periodos.produzidos.length > 0,
          temAtrasados: listaAtrasados.length > 0,
        }),
      },
    };
  }, [pronto, painel, periodos, hoje, mostraPeriodos, listaAtrasados, simulacao.entrada, simulacao.resultado]);

  useExportSnapshot(PAINEL_EXPORTACAO, exportacao, pageRef);

  if (estado === "carregando") return <PageLoader scope="kpis" />;
  if (estado === "erro_planilha") return <EmptyStateErro erro={erroPlanilha} />;
  if (estado === "sem_planilha") {
    return (
      <Pagina>
        <SemPlanilha
          podeLerPlanilha={hasPermission?.(PERM_LER_PLANILHA)}
          temLinhas={planilha.temLinhas}
          onAbrirPlanilhas={() => navigate(ROTA_PLANILHAS)}
        />
      </Pagina>
    );
  }
  if (estado === "layout") {
    return (
      <Pagina>
        <LayoutNaoReconhecido painel={painel} />
      </Pagina>
    );
  }
  if (estado === "erro_calendario") {
    return <EmptyStateErro titulo="Não foi possível ler o calendário da unidade" erro={calendario.erro} onRetry={recarregar} />;
  }
  if (estado === "erro_historico") {
    return <EmptyStateErro titulo="Não foi possível ler o histórico da planilha" erro={historico.erro} onRetry={recarregar} />;
  }

  return (
    <ReprogramarContext.Provider value={podeReprogramar && pronto ? abrirCenario : null}>
      <Pagina ref={pageRef}>
        <Topo>
          <LinhaTitulo>
            <BlocoTitulo>
              <h1>Capacidade da Fábrica</h1>
              <p>{subtituloDaPlanilha(planilha)}</p>
            </BlocoTitulo>
            <Controles>
              <BotaoAcao type="button" aria-label="Simular venda" onClick={() => setSimulacaoAberta(true)}>
                <FiTrendingUp size={15} aria-hidden="true" />
                <span className="rotulo">Simular venda</span>
              </BotaoAcao>
              {podeReprogramar ? (
                <BotaoAcao type="button" aria-label="Cenário do PCP" onClick={() => abrirCenario(null)}>
                  <FiRepeat size={15} aria-hidden="true" />
                  <span className="rotulo">Cenário do PCP</span>
                </BotaoAcao>
              ) : null}
              <SoIcone>
                <BotaoExportacao
                  titulo={exportacao.titulo}
                  resumo={exportacao.resumo}
                  secoes={exportacao.secoes}
                  visual={exportacao.visual}
                  label="Exportar"
                />
              </SoIcone>
            </Controles>
          </LinhaTitulo>
          <SeletorOpcoes opcoes={abas} valor={aba} onChange={setAba} rotulo="Seções da página" papel="tablist" />
        </Topo>

        <FaixaIndicadores indicadores={indicadores} />
        {segundaLeitura ? (
          <Regua role="note" aria-label="Segunda leitura">
            {segundaLeitura}
          </Regua>
        ) : null}
        <Avisos painel={painel} />
        {aba !== ABA_VENDAS ? (
          <AvisoDeSugestoes quantidade={paineisDeVenda.quantasSugeridas} onVer={() => setAba(ABA_VENDAS)} />
        ) : null}
        {!simulacaoAberta ? (
          <SimulacaoAtiva
            entrada={simulacao.entrada}
            resultado={simulacao.resultado}
            onAbrir={() => setSimulacaoAberta(true)}
            onLimpar={simulacao.limpar}
          />
        ) : null}
        <BlocoAtrasados itens={listaAtrasados} expandirTudo={renderAll} podeAbrir={podeAbrir} onAbrir={abrirAmbiente} />
        {podeReprogramar ? <BlocoConferencia itens={marcas.itens} onEsquecer={marcas.esquecer} /> : null}

        {aba === ABA_OCUPACAO ? (
          <>
            <LinhaControles>
              <SeletorOpcoes opcoes={ESCALAS} valor={escala} onChange={setEscala} rotulo="Escala" />
            </LinhaControles>
            {!mostraPeriodos ? (
              <VisaoDia
                painel={painel}
                hoje={hoje}
                podeVerMapa={hasPermission?.(PERM_MAPA)}
                podeAbrir={podeAbrir}
                onAbrir={abrirAmbiente}
              />
            ) : (
              <>
                <ReguaDaOcupacao precisao={painel.precisao} />
                <Periodos
                  periodos={periodos}
                  compacto={compacto}
                  expandirTudo={renderAll}
                  podeAbrir={podeAbrir}
                  onAbrir={abrirAmbiente}
                />
              </>
            )}
          </>
        ) : null}

        {aba === ABA_VENDAS ? (
          <AbaVendasAssinadas
            vendas={vendas}
            sugestoes={paineisDeVenda.sugestoes}
            podeRegistrar={paineisDeVenda.podeRegistrar}
            podeLigar={paineisDeVenda.podeLigar}
            podeCancelarDe={paineisDeVenda.podeCancelarDe}
            onRegistrar={paineisDeVenda.abrirRegistro}
            onLigar={paineisDeVenda.setLigando}
            onCancelar={paineisDeVenda.setCancelando}
          />
        ) : null}

        {aba === "qualidade" ? (
          <AbaQualidade qualidade={painel.qualidade} expandirTudo={renderAll} podeAbrir={podeAbrir} onAbrir={abrirAmbiente} />
        ) : null}

        {aba === ABA_EXCECOES ? (
          <AbaExcecoes
            painel={painel}
            excecoes={excecoes}
            linhaDaPlanilha={linhaDaPlanilha}
            podeAbrir={podeAbrir}
            onAbrir={abrirAmbiente}
          />
        ) : null}

        {aba === "precisao" ? (
          <AbaPrecisao
            precisao={painel.precisao}
            fotos={historico.fotos}
            hoje={hoje}
            compacto={compacto}
            podeImportar={hasPermission?.(PERM_CONFIGURAR_PLANILHA)}
            onImportar={importarCopia}
          />
        ) : null}

        <AmbienteDetalhesDrawer ambiente={ambienteAberto} onClose={() => setAmbienteAberto(null)} />
        <SimularVenda
          aberto={simulacaoAberta}
          onFechar={() => setSimulacaoAberta(false)}
          entrada={simulacao.entrada}
          resultado={simulacao.resultado}
          link={simulacao.link}
          linkInvalido={simulacao.linkInvalido}
          onDefinir={simulacao.definir}
          onRegistrarVenda={paineisDeVenda.podeRegistrar ? registrarDaSimulacao : null}
          hoje={hoje}
        />
        <RegistrarVendaAssinada
          aberto={paineisDeVenda.registro.aberto}
          onFechar={paineisDeVenda.fecharRegistro}
          formInicial={paineisDeVenda.registro.form}
          onRegistrar={paineisDeVenda.registrar}
          hoje={hoje}
        />
        <LigarVendaAssinada
          venda={paineisDeVenda.ligando}
          onFechar={() => paineisDeVenda.setLigando(null)}
          linhas={painelPronto?.classificacao?.linhas}
          sugeridos={paineisDeVenda.ligando ? paineisDeVenda.sugestoes[paineisDeVenda.ligando.id] : undefined}
          onLigar={vendas.ligar}
        />
        <CancelarVendaAssinada
          venda={paineisDeVenda.cancelando}
          onFechar={() => paineisDeVenda.setCancelando(null)}
          onCancelar={vendas.cancelar}
        />
        {podeReprogramar ? (
          <CenarioPcp
            aberto={cenario.aberto}
            onFechar={() => setCenario((atual) => ({ ...atual, aberto: false }))}
            pedidoInicial={cenario.pedido}
            painel={painelPronto}
            hoje={hoje}
            G={folga.G}
            feriadosPlanilha={feriadosPlanilha}
            tabelasAusente={tabelasAusente}
            anosComRegistro={anosComRegistro}
            escala={escalaDosPeriodos}
            onMarcar={marcas.marcar}
          />
        ) : null}
      </Pagina>
    </ReprogramarContext.Provider>
  );
}
